import React, { useEffect } from 'react';
import SEO from './SEO';
import { Link } from 'react-router-dom';

const Opus3DPrivacyPolicy: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            title: "1. Introduction",
            content: (
                <div className="space-y-4">
                    <p>
                        OmniviewLabs ("we", "us", or "our") operates the Opus 3D mobile and web application. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our application.
                    </p>
                    <p>
                        By using Opus 3D, you agree to the collection and use of information in accordance with this policy. If you do not agree with these terms, please do not use the application.
                    </p>
                </div>
            )
        },
        {
            title: "2. Information We Collect",
            content: (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-bold text-[#021226] mb-2">2.1 Account and Identity Information</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Username and password (credentials are authenticated via AWS Cognito and never stored in plain text)</li>
                            <li>User role and access level within your organisation</li>
                            <li>Team or organisation name (client name / client ID)</li>
                            <li>Last login timestamp</li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-[#021226] mb-2">2.2 Usage and Activity Data</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Pages visited, features used, and actions taken within the app</li>
                            <li>Session start and end times, session duration</li>
                            <li>Recent activity history per module (home, inventory, studio, sales, store, CRM, configurator)</li>
                            <li>Notification history</li>
                            <li>App state preferences (layout, filters, design settings)</li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-[#021226] mb-2">2.3 Device and Technical Information</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>IP address (collected at session start for security logging)</li>
                            <li>Device type, operating system, and browser / app user-agent string</li>
                            <li>A device UUID generated locally for session identification</li>
                            <li>Display mode (Android app, web app, or browser)</li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-[#021226] mb-2">2.4 Business and Transaction Data</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Product configurations, 3D design projects, and saved quotations you create</li>
                            <li>Orders, purchase history, and payment records</li>
                            <li>CRM records: leads, customer profiles, support tickets, and sales activities</li>
                            <li>Inventory items, materials, and catalogue data you manage</li>
                            <li>Design studio assets: flyers, social-media posts, and uploaded images</li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-[#021226] mb-2">2.5 Content and Media You Upload</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Photos captured or selected from your device for AI-powered room analysis and product suggestions</li>
                            <li>Images and assets uploaded to the design studio or product catalogue</li>
                            <li>Files associated with 3D models or configurations</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "3. Device Permissions",
            content: (
                <div className="space-y-4">
                    <p>The app may request the following device permissions:</p>
                    <div className="overflow-x-auto">
                        <table className="min-w-full border-collapse border border-slate-200">
                            <thead>
                                <tr className="bg-slate-50 text-left">
                                    <th className="border border-slate-200 p-3 text-sm font-bold text-[#021226]">Permission</th>
                                    <th className="border border-slate-200 p-3 text-sm font-bold text-[#021226]">Purpose</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm">
                                <tr>
                                    <td className="border border-slate-200 p-3 font-semibold">Camera</td>
                                    <td className="border border-slate-200 p-3">Capture room photos for the AI Quick Design feature and product image uploads</td>
                                </tr>
                                <tr>
                                    <td className="border border-slate-200 p-3 font-semibold">Storage / Media</td>
                                    <td className="border border-slate-200 p-3">Select images from your gallery for design uploads and AI analysis</td>
                                </tr>
                                <tr>
                                    <td className="border border-slate-200 p-3 font-semibold">Notifications</td>
                                    <td className="border border-slate-200 p-3">Send local app notifications (order updates, alerts)</td>
                                </tr>
                                <tr>
                                    <td className="border border-slate-200 p-3 font-semibold">Internet</td>
                                    <td className="border border-slate-200 p-3">Core functionality — required to connect to backend services</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="text-sm text-slate-500 italic">
                        Permissions are requested only when a relevant feature is used. You can revoke any permission at any time via your device settings; doing so will disable the associated feature.
                    </p>
                </div>
            )
        },
        {
            title: "4. How We Use Your Information",
            content: (
                <ul className="list-disc pl-5 space-y-2">
                    <li><strong>Authentication & security:</strong> Verify your identity, manage your session, and protect your account.</li>
                    <li><strong>Core features:</strong> Provide interactive 3D product configuration, AI-powered design suggestions, inventory management, sales tracking, and CRM functionality.</li>
                    <li><strong>Personalisation:</strong> Remember your preferences, recent activity, and app state across sessions.</li>
                    <li><strong>Quotations & orders:</strong> Generate quotations and maintain order history.</li>
                    <li><strong>Analytics & improvement:</strong> Understand how features are used to improve the application.</li>
                    <li><strong>Support:</strong> Diagnose errors and respond to support requests.</li>
                    <li><strong>Legal compliance:</strong> Meet applicable legal and regulatory obligations.</li>
                </ul>
            )
        },
        {
            title: "5. Third-Party Services",
            content: (
                <div className="space-y-6">
                    <p>We use the following third-party services that may receive your data:</p>
                    <div>
                        <h3 className="text-lg font-bold text-[#021226] mb-2">5.1 Amazon Web Services (AWS)</h3>
                        <ul className="list-disc pl-5 space-y-1 mb-2">
                            <li>AWS Cognito — User authentication and password management. Your credentials are handled directly by Cognito; we never store your password.</li>
                            <li>AWS API Gateway — All API requests pass through AWS infrastructure hosted in the ap-south-1 (Mumbai) region.</li>
                            <li>AWS S3 — Storage for 3D model files, product images, templates, and design assets.</li>
                        </ul>
                        <p className="text-sm">AWS Privacy Policy: <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-[#FF5C00] hover:underline">https://aws.amazon.com/privacy/</a></p>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-[#021226] mb-2">5.2 Google Services</h3>
                        <ul className="list-disc pl-5 space-y-1 mb-2">
                            <li>Google Generative AI — Processes room photos you upload to suggest relevant furniture and product configurations for the AI Quick Design feature. Images are sent to Google's API servers.</li>
                            <li>Google Sheets API — Optional export of order data to Google Sheets when you use this feature.</li>
                        </ul>
                        <p className="text-sm">Google Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#FF5C00] hover:underline">https://policies.google.com/privacy</a></p>
                    </div>
                </div>
            )
        },
        {
            title: "6. Data Storage and Retention",
            content: (
                <div className="space-y-4">
                    <p>Your data is stored on servers located in India (AWS ap-south-1 region).</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Account and business data is retained for as long as your account is active.</li>
                        <li>Session logs and activity data are retained for a limited period for security and debugging purposes.</li>
                        <li>Uploaded media is retained until you delete it or your account is closed.</li>
                        <li>On your device, data is stored in browser localStorage and cleared when you log out or uninstall the app.</li>
                    </ul>
                </div>
            )
        },
        {
            title: "7. Subscription and Billing",
            content: (
                <div className="space-y-4">
                    <p>
                        Opus 3D is available on a free tier as well as paid monthly and annual subscription plans. Subscription plan details, usage credits, and credit transaction history are stored on our servers and associated with your account.
                    </p>
                    <p>
                        No payment processing happens within the app. Paid subscriptions are arranged by contacting our team directly via email, phone, or WhatsApp. We do not collect, store, or process any payment card details, bank information, or UPI credentials through the application.
                    </p>
                </div>
            )
        },
        {
            title: "8. Data Sharing",
            content: (
                <div className="space-y-4">
                    <p>We do not sell, rent, or trade your personal information to third parties. We may share data:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>With the third-party service providers listed in Section 5, solely to operate app functionality.</li>
                        <li>With members of your own organisation who share the same account, consistent with your assigned role.</li>
                        <li>When required by law, court order, or regulatory authority.</li>
                        <li>In the event of a merger, acquisition, or transfer of assets (with advance notice to users).</li>
                    </ul>
                </div>
            )
        },
        {
            title: "9. Data Security",
            content: (
                <div className="space-y-4">
                    <p>We implement industry-standard security measures including:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>HTTPS / TLS encryption for all data in transit.</li>
                        <li>AWS Cognito for secure credential management — passwords are never stored in plain text.</li>
                        <li>JWT-based session tokens with expiry and revocation.</li>
                        <li>Role-based access control to limit data access within organisations.</li>
                        <li>IP logging and session tracking to detect unauthorised access.</li>
                    </ul>
                    <p className="text-sm text-slate-500 italic">
                        No method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
                    </p>
                </div>
            )
        },
        {
            title: "10. Children's Privacy",
            content: (
                <p>
                    Opus 3D is intended for business use and is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will delete it promptly.
                </p>
            )
        },
        {
            title: "11. Your Rights",
            content: (
                <div className="space-y-4">
                    <p>Depending on your jurisdiction, you may have the right to:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Access the personal data we hold about you.</li>
                        <li>Correct inaccurate or incomplete data.</li>
                        <li>Delete your account and associated personal data.</li>
                        <li>Restrict or object to certain types of processing.</li>
                        <li>Data portability — receive your data in a structured, machine-readable format.</li>
                        <li>Withdraw consent for optional processing at any time.</li>
                    </ul>
                    <p>To request deletion of your account and associated personal data, visit our <Link to="/opus3d-delete-account" className="text-[#FF5C00] hover:underline font-bold">Account Deletion page</Link>. For all other rights requests, please contact us at <a href="mailto:info@omniviewlabs.com" className="text-[#FF5C00] hover:underline font-bold">info@omniviewlabs.com</a>. We will respond within 30 days.</p>
                </div>
            )
        },
        {
            title: "12. Cookies and Local Storage",
            content: (
                <div className="space-y-4">
                    <p>
                        The app uses browser localStorage to store your session token, user preferences, cart data, and recent activity. This data remains on your device and is not transmitted to third parties except as part of normal API requests.
                    </p>
                    <p>
                        No advertising cookies or cross-site tracking technologies are used.
                    </p>
                </div>
            )
        },
        {
            title: "13. Changes to This Policy",
            content: (
                <p>
                    We may update this Privacy Policy from time to time. Changes will be posted within the app and on this page with an updated "Last updated" date. Continued use of the app after changes constitutes your acceptance of the revised policy. For significant changes, we will provide a more prominent notice (such as an in-app notification).
                </p>
            )
        },
        {
            title: "14. Contact Us",
            content: (
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100">
                    <p className="font-bold text-[#021226] mb-2">OmniviewLabs</p>
                    <p className="flex items-center gap-2 mb-1">
                        <span className="text-slate-400 font-medium">Email:</span>
                        <a href="mailto:info@omniviewlabs.com" className="text-[#FF5C00] font-bold hover:underline">info@omniviewlabs.com</a>
                    </p>
                </div>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Opus 3D Privacy Policy | OmniView Labs"
                description="Privacy Policy for Opus 3D mobile and web application. Learn how we protect and manage your data."
                canonical="/opus3d-privacy"
            />
            
            {/* Header / Hero Section */}
            <div className="pt-32 pb-16 bg-[#F8FAFC] border-b border-slate-100 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-[0.2] pointer-events-none"></div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF5C00]/10 text-[#FF5C00] text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.9L10 1.55l7.834 3.35a1 1 0 01.666.927v4.248a10.45 10.45 0 01-5.11 8.98l-3.057 1.747a1 1 0 01-.666 0l-3.057-1.747A10.45 10.45 0 012.166 10.125V5.827a1 1 0 01.666-.927zM10 3.16L4.166 5.657v4.468a8.45 8.45 0 004.134 7.27l1.7 0.97 1.7-0.97a8.45 8.45 0 004.134-7.27V5.657L10 3.16z" clipRule="evenodd" /></svg>
                        Legal Compliance
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-[#021226] mb-6 tracking-tight">Privacy Policy</h1>
                    <p className="text-lg text-slate-500 font-medium mb-2">Opus 3D Mobile & Web Application</p>
                    <p className="text-sm text-slate-400 font-bold uppercase tracking-widest">Last updated: May 20, 2026</p>
                </div>
            </div>

            <div className="container mx-auto px-6 py-20">
                <div className="max-w-4xl mx-auto">
                    <div className="space-y-16">
                        {sections.map((section, index) => (
                            <section key={index} className="scroll-mt-32">
                                <h2 className="text-2xl md:text-3xl font-black text-[#021226] mb-6 tracking-tight flex items-center gap-4">
                                    <span className="w-1.5 h-8 bg-[#FF5C00] rounded-full hidden md:block"></span>
                                    {section.title}
                                </h2>
                                <div className="prose prose-slate prose-lg max-w-none text-slate-600 leading-relaxed font-medium">
                                    {section.content}
                                </div>
                            </section>
                        ))}
                    </div>

                    {/* Footer / Back CTA */}
                    <div className="mt-24 pt-12 border-t border-slate-100 text-center">
                        <p className="text-sm text-slate-400 mb-8 font-bold uppercase tracking-widest">
                            &copy; 2026 OmniviewLabs. All rights reserved.
                        </p>
                        <Link 
                            to="/" 
                            className="inline-flex items-center gap-3 px-8 py-4 bg-[#021226] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[#003B5C] transition-all active:scale-95 shadow-xl"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Opus3DPrivacyPolicy;
